/**
 * shell/dock.ts —— 停靠面**状态**（每会话一份；纯函数，动作＝铸造 id → 规划 → 记录 → 整体赋回）。
 *
 * 真源：`102:frontend_src/src/rightbar/{useSurface,dockPlanner}.js` —— 抄语义，只取一期所需面。
 * 依据：设计件 §6.1（**每会话一个停靠面**；tab 布局**不持久**——刷新回折叠默认；切会话各停靠面
 *       留原处）／§6.1.1（**展开态永远至少一个 tab、永远没有空格**；根格被清空 ⇒ **填默认页**；
 *       折叠态**不回填**：会话可以「折叠且空」地开始，**首次展开才播种默认页**）。
 *
 * 🔴 纪律：
 *   · 无就地编辑：每个动作返回**新状态**（记录序列可回放）；
 *   · 默认页取**声明**（`tabRegistry.defaultKind()`）——不在本件硬编码类型名；
 *   · 末个 tab 关闭 ⇒ **立即补默认页**（承「永远至少一个 tab」，不出现空栏）。
 */
import { claim, defaultKind, getRegistry, titleOf, FALLBACK_TAB_KIND } from "../components/rightbar/tabRegistry";

export interface DockTab {
  tabId: string;
  kind: string;
  title: string;
  /** 地址即身份（§6.3）：地址驱动类型（text）按它取数；页类型（files/progress）为空 */
  address?: string;
  /** 承接：兜底页（唯一 tab 时）不可关 */
  closable: boolean;
}

export interface DockState {
  tabs: DockTab[];
  activeTabId: string | null;
  /** id 铸造计数（承源件「已铸造 id 的记录序列 ＋ id 计数」） */
  seq: number;
}

export const EMPTY_DOCK: DockState = { tabs: [], activeTabId: null, seq: 0 };

function mint(state: DockState, kind: string, address = ""): DockTab {
  const seq = state.seq + 1;
  return {
    tabId: `${kind}-${seq}`,
    kind,
    title: titleOf(kind, address),
    address: address || undefined,
    closable: kind !== FALLBACK_TAB_KIND || state.tabs.length > 1,
  };
}

/** 唯一 tab 时兜底页不可关（其余 tab 可关） */
function withClosable(tabs: DockTab[]): DockTab[] {
  return tabs.map((t) => ({ ...t, closable: !(tabs.length === 1 && t.kind === FALLBACK_TAB_KIND) }));
}

/** **首次展开播种**：空 ⇒ 补声明默认页；非空 ⇒ 原样（折叠态不回填，播种只在展开时调） */
export function seedDefault(state: DockState): DockState {
  if (state.tabs.length) return state;
  const kind = defaultKind();
  const tab = mint(state, kind);
  return { tabs: [tab], activeTabId: tab.tabId, seq: state.seq + 1 };
}

/** 按 kind 开页（已在 ⇒ 聚焦不重开；`text` 无地址 ⇒ 认领否决 ⇒ 落兜底页） */
export function openKind(state: DockState, kind: string, address = ""): DockState {
  const { kind: claimedKind, matched } = claim(address, kind);
  const hit = state.tabs.find((t) => t.kind === claimedKind && (t.address ?? "") === (address || ""));
  if (hit && matched) return { ...state, activeTabId: hit.tabId };
  const tab = mint(state, claimedKind, address);
  return { tabs: withClosable([...state.tabs, tab]), activeTabId: tab.tabId, seq: state.seq + 1 };
}

/**
 * 按地址开页（走候选排序认领）。
 * ★20261011 PFK4·L-PF-B12（规划件 §7.3 #18）：**不加 `multiple` 字段**，只补「同一 (kind, 地址)」唯一规则
 * ——复用 `openKind` 的去重：同 kind＋同地址 ⇒ **聚焦既有 tab**（判据③：同文件点两次只多一个 tab）。
 */
export function openAddress(state: DockState, address: string, kind?: string): DockState {
  const { kind: claimedKind, matched } = claim(address, kind);
  const hit = state.tabs.find((t) => t.kind === claimedKind && (t.address ?? "") === (address || ""));
  if (hit && matched) return { ...state, activeTabId: hit.tabId };
  const tab = mint(state, claimedKind, address);
  return { tabs: withClosable([...state.tabs, tab]), activeTabId: tab.tabId, seq: state.seq + 1 };
}

/** 关 tab：末个关闭 ⇒ **补默认页**（不出现空栏）；活跃转移至相邻 */
export function closeTab(state: DockState, tabId: string): DockState {
  const idx = state.tabs.findIndex((t) => t.tabId === tabId);
  if (idx < 0) return state;
  const rest = state.tabs.filter((t) => t.tabId !== tabId);
  if (!rest.length) {
    const kind = defaultKind();
    const tab = mint({ ...state, tabs: [] }, kind);
    return { tabs: [tab], activeTabId: tab.tabId, seq: state.seq + 1 };
  }
  const active = state.activeTabId === tabId ? rest[Math.min(idx, rest.length - 1)].tabId : state.activeTabId;
  return { tabs: withClosable(rest), activeTabId: active, seq: state.seq };
}

/** 聚焦 tab */
export function focusTab(state: DockState, tabId: string): DockState {
  return state.tabs.some((t) => t.tabId === tabId) ? { ...state, activeTabId: tabId } : state;
}

/** tab 类型注册表的 kind 集合（供一致性判据取；禁第二处列字面量） */
export function registeredKinds(): string[] {
  return getRegistry().map((r) => r.kind);
}
